import Dialog from '@mui/material/Dialog';
import { useSmartListsSectionOpen } from 'features/user';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconFormNames } from 'shared/assets/icons';
import { SmartListForm } from 'shared/components/ui/smart-list-form';
import { defaultSmartListColorOption } from 'shared/constants';
import { useCreateSmartListMutation } from 'shared/hooks/queries/use-create-smart-list-mutation';
import { useUpdateSmartListMutation } from 'shared/hooks/queries/use-update-smart-list-mutation';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import { FilterDocument } from 'shared/types/filter-document';
import { NewSmartList, SmartList } from 'shared/types/smart-list';
import { SmartListFormFields } from 'shared/types/smart-list-form';

export type SmartListFormDialogProps = {
  open: boolean;
  onClose: () => void;
  filterDocument?: FilterDocument;
  smartList?: SmartList;
};

export const SmartListFormDialog: React.FC<SmartListFormDialogProps> = ({
  open,
  onClose,
  filterDocument,
  smartList,
}) => {
  const user = useUser();
  const navigate = useNavigate();
  const { mutateAsync: createSmartList } = useCreateSmartListMutation();
  const { mutateAsync: updateSmartList } = useUpdateSmartListMutation();
  const [, setSmartListsSectionOpen] = useSmartListsSectionOpen();

  const initialValues = useMemo<SmartListFormFields | undefined>(() => {
    if (smartList) {
      return {
        name: smartList.name,
        color: smartList.color ?? defaultSmartListColorOption.value,
        icon: smartList.icon ?? IconFormNames.SmartList,
      };
    }
    return undefined;
  }, [smartList]);

  const handleSubmit = async (values: SmartListFormFields) => {
    if (!user) {
      return;
    }

    if (smartList) {
      await updateSmartList({
        uid: user.id,
        smartList: {
          id: smartList.id,
          name: values.name,
          icon: values.icon,
          color: values.color,
          filterDocument: filterDocument ?? smartList.filterDocument,
        },
      });
      onClose();
      return;
    }

    if (filterDocument) {
      const newSmartList: NewSmartList = {
        name: values.name!, // Guaranteed by validation
        filterDocument,
        icon: values.icon,
        color: values.color,
      };
      const createdSmartList = await createSmartList(newSmartList);
      setSmartListsSectionOpen(true);
      navigate(Paths.SmartListDetail.replace(':id', createdSmartList.id));
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <SmartListForm
        isEdit={!!smartList}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={onClose}
      />
    </Dialog>
  );
};
